import { format } from "date-fns";
import { fromZonedTime, toZonedTime, formatInTimeZone, getTimezoneOffset } from "date-fns-tz";

/**
 * Predefined timezone display names with proper formatting
 */
const TIMEZONE_DISPLAY_NAMES = {
  // US/Canada Major Zones
  "America/New_York": "Eastern Time (US & Canada)",
  "America/Chicago": "Central Time (US & Canada)", 
  "America/Denver": "Mountain Time (US & Canada)",
  "America/Los_Angeles": "Pacific Time (US & Canada)",
  "America/Anchorage": "Alaska Time (US & Canada)",
  "Pacific/Honolulu": "Hawaii Time (US & Canada)",
  "America/Phoenix": "Arizona Time (US & Canada)",
  "America/Toronto": "Eastern Time (Canada)",
  "America/Vancouver": "Pacific Time (Canada)",
  "America/Edmonton": "Mountain Time (Canada)",
  "America/Winnipeg": "Central Time (Canada)",
  "America/Halifax": "Atlantic Time (Canada)",
  "America/St_Johns": "Newfoundland Time (Canada)",
  
  // Mexico
  "America/Mexico_City": "Central Time (Mexico)",
  "America/Tijuana": "Pacific Time (Mexico)",
  "America/Cancun": "Eastern Time (Mexico)",
  
  // South America
  "America/Sao_Paulo": "Brasília Time",
  "America/Argentina/Buenos_Aires": "Argentina Time",
  "America/Lima": "Peru Time",
  "America/Bogota": "Colombia Time",
  "America/Santiago": "Chile Time",
  
  // Europe
  "UTC": "Coordinated Universal Time",
  "Europe/London": "Greenwich Mean Time",
  "Europe/Dublin": "Dublin Time", 
  "Europe/Paris": "Central European Time",
  "Europe/Berlin": "Central European Time",
  "Europe/Rome": "Central European Time",
  "Europe/Madrid": "Central European Time",
  "Europe/Amsterdam": "Central European Time",
  "Europe/Brussels": "Central European Time",
  "Europe/Vienna": "Central European Time",
  "Europe/Zurich": "Central European Time",
  "Europe/Stockholm": "Central European Time",
  "Europe/Oslo": "Central European Time",
  "Europe/Copenhagen": "Central European Time",
  "Europe/Helsinki": "Eastern European Time",
  "Europe/Athens": "Eastern European Time",
  "Europe/Istanbul": "Turkey Time",
  "Europe/Moscow": "Moscow Time",
  
  // Asia
  "Asia/Tokyo": "Japan Standard Time",
  "Asia/Seoul": "Korea Standard Time",
  "Asia/Shanghai": "China Standard Time",
  "Asia/Hong_Kong": "Hong Kong Time",
  "Asia/Singapore": "Singapore Time",
  "Asia/Bangkok": "Indochina Time",
  "Asia/Jakarta": "Western Indonesia Time",
  "Asia/Manila": "Philippine Time",
  "Asia/Kuala_Lumpur": "Malaysia Time",
  "Asia/Kolkata": "India Standard Time",
  "Asia/Dubai": "Gulf Standard Time",
  "Asia/Riyadh": "Arabia Standard Time",
  "Asia/Tehran": "Iran Standard Time",
  
  // Australia & Pacific  
  "Australia/Sydney": "Australian Eastern Time",
  "Australia/Melbourne": "Australian Eastern Time",
  "Australia/Brisbane": "Australian Eastern Time",
  "Australia/Perth": "Australian Western Time",
  "Australia/Adelaide": "Australian Central Time",
  "Australia/Darwin": "Australian Central Time",
  "Pacific/Auckland": "New Zealand Time",
  "Pacific/Fiji": "Fiji Time",
  "Pacific/Tahiti": "Tahiti Time",
  
  // Africa
  "Africa/Johannesburg": "South Africa Time",
  "Africa/Cairo": "Eastern European Time",
  "Africa/Lagos": "West Africa Time",
  "Africa/Nairobi": "East Africa Time",
  "Africa/Casablanca": "Western European Time"
};

/**
 * Get a friendly display name for a timezone
 */
const getDisplayName = (timezone) => {
  // Return predefined name or create one from timezone path
  if (TIMEZONE_DISPLAY_NAMES[timezone]) {
    return TIMEZONE_DISPLAY_NAMES[timezone];
  }
  
  // Fallback: create name from timezone path
  const parts = timezone.split('/');
  if (parts.length >= 2) {
    const region = parts[0];
    const city = parts[parts.length - 1].replace(/_/g, ' ');
    
    if (region === 'America') {
      return `${city} Time`;
    } else if (region === 'Europe') {
      return `${city} Time`;
    } else if (region === 'Asia') {
      return `${city} Time`;
    } else if (region === 'Australia') {
      return `${city} Time`;
    } else if (region === 'Africa') {
      return `${city} Time`;
    } else if (region === 'Pacific') {
      return `${city} Time`;
    }
  }
  
  return timezone.replace(/_/g, ' ');
};

/**
 * Create a timezone option object with formatted label
 */
const createTimezoneOption = (timezone) => {
  let offset = 0;
  
  try {
    // Use date-fns-tz to get the timezone offset
    offset = getTimezoneOffset(timezone, new Date()) / (1000 * 60 * 60);
  } catch (error) {
    // Fallback: calculate offset using Intl API
    try {
      const now = new Date();
      const targetTime = new Date(now.toLocaleString("en-US", {timeZone: timezone}));
      const localTime = new Date(now.toLocaleString("en-US"));
      offset = (localTime.getTime() - targetTime.getTime()) / (1000 * 60 * 60);
    } catch (fallbackError) {
      console.warn(`Error processing timezone ${timezone}:`, error);
      offset = 0;
    }
  }

  // Format offset as +/-HH:MM for GMT display
  const offsetHours = Math.floor(Math.abs(offset));
  const offsetMinutes = Math.round((Math.abs(offset) - offsetHours) * 60);
  const offsetSign = offset >= 0 ? "+" : "-";
  const gmtString = `GMT${offsetSign}${offsetHours.toString().padStart(2, "0")}:${offsetMinutes.toString().padStart(2, "0")}`;
  
  // Create display name based on timezone
  const displayName = getDisplayName(timezone);

  const label = `(${gmtString}) ${displayName}`;

  return {
    value: timezone,
    label,
    offset,
    displayName
  };
};

/**
 * Get user's timezone using browser API
 * Falls back to UTC-8 (Pacific Time) if detection fails
 */
export const detectUserTimezone = () => {
  try {
    const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    
    // Validate the timezone
    if (isValidTimezone(browserTimezone)) {
      return browserTimezone;
    }
  } catch (error) {
    console.warn("Failed to detect user timezone:", error);
  }
  
  // Fallback to Pacific Time (UTC-8)
  return "America/Los_Angeles";
};

/**
 * Get timezone display name for a given timezone value
 */
export const getTimezoneDisplayName = (timezoneValue) => {
  if (!timezoneValue) return "";
  
  try {
    const option = createTimezoneOption(timezoneValue);
    return option.label;
  } catch (error) {
    return timezoneValue;
  }
};

/**
 * Get current time in a specific timezone
 */
export const getCurrentTimeInTimezone = (timezone) => {
  try {
    const now = new Date();
    return formatInTimeZone(now, timezone, "yyyy-MM-dd HH:mm:ss");
  } catch (error) {
    console.warn("Failed to get time for timezone:", timezone, error);
    return format(new Date(), "yyyy-MM-dd HH:mm:ss");
  }
};

/**
 * Convert a date from one timezone to UTC
 */
export const convertToUtc = (date, fromTimezone) => {
  try {
    return fromZonedTime(date, fromTimezone);
  } catch (error) {
    console.warn("Failed to convert to UTC:", error);
    return date;
  }
};

/**
 * Convert a UTC date to a specific timezone
 */
export const convertFromUtc = (utcDate, toTimezone) => {
  try {
    return toZonedTime(utcDate, toTimezone);
  } catch (error) {
    console.warn("Failed to convert from UTC:", error);
    return utcDate;
  }
};

/**
 * Validate if a timezone string is valid
 */
export const isValidTimezone = (timezone) => {
  try {
    Intl.DateTimeFormat(undefined, { timeZone: timezone });
    return true;
  } catch (error) {
    return false;
  }
};

/**
 * Get a flat list of timezone options sorted by GMT offset
 */
export const getFlatTimezoneOptions = () => {
  let allTimezones;
  
  try {
    // Modern browsers support this
    allTimezones = Intl.supportedValuesOf('timeZone');
  } catch (error) {
    // Fallback to common timezones for older browsers
    allTimezones = [
      "America/New_York", "America/Chicago", "America/Denver", "America/Los_Angeles",
      "America/Anchorage", "Pacific/Honolulu", "America/Phoenix", "America/Toronto",
      "America/Vancouver", "America/Mexico_City", "America/Sao_Paulo", 
      "America/Argentina/Buenos_Aires", "UTC", "Europe/London", "Europe/Paris",
      "Europe/Berlin", "Europe/Rome", "Europe/Madrid", "Europe/Amsterdam",
      "Europe/Moscow", "Asia/Tokyo", "Asia/Shanghai", "Asia/Hong_Kong",
      "Asia/Singapore", "Asia/Seoul", "Asia/Kolkata", "Asia/Dubai",
      "Australia/Sydney", "Australia/Melbourne", "Australia/Perth",
      "Pacific/Auckland", "Africa/Johannesburg", "Africa/Cairo", "Africa/Lagos"
    ];
  }

  // Convert all timezones to options and sort by GMT offset
  const options = allTimezones
    .map(timezone => createTimezoneOption(timezone))
    .sort((a, b) => {
      // Sort by offset first, then by label
      if (a.offset !== b.offset) {
        return a.offset - b.offset;
      }
      return a.label.localeCompare(b.label);
    });
  
  return options;
};